if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.input = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector(
        '.js-results-container'
      );
      this.btnSubmit = this.querySelector('.js-btn-submit');
      this.btnClearText = this.querySelector('.js-btn-clear-text');
      this.errorMessageContainer = this.querySelector(
        '.js-message-error'
      );
      this.errorMessageText =
        this.errorMessageContainer.querySelector('[data-text]');
      this.timeout = null;
      this.isOpen = false;
      this.drawer = this.closest('.js-drawer');

      var searchDrawerContent = this.closest('.drawer__content--search');
      var searchSuggestionSections = searchDrawerContent?.querySelectorAll(
        '.search-drawer__suggestions, .search-most-loved-styles'
      ) || [];

      var toggleSearchSuggestionSections = (visible) => {
        searchSuggestionSections.forEach(section => {
          section.style.display = visible ? '' : 'none';
        });
      };

      this.input.addEventListener('input', e => {
        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
          const searchTerm = this.input.value.trim();

          if (searchTerm.length <= 0) {
            this.close();
            if(DeviceDetector.isMobile() || DeviceDetector.isTablet()) {
              searchDrawerContent.style.height = 'auto';
            }
            toggleSearchSuggestionSections(true);
            return;
          }

          this.isOpen = true;
          this.getSearchResults(searchTerm);
          if(DeviceDetector.isMobile() || DeviceDetector.isTablet()) {
            searchDrawerContent.style.height = '100%';
          }
          toggleSearchSuggestionSections(false);
        }, 300);
      });

      this.input.addEventListener('keydown', e => {
        const isEscape = e.key === 'Escape';

        if (!isEscape) {
          return;
        }

        if (!this.input.value) {
          if (this.drawer) {
            this.drawer.parentElement.parentElement.toggleDrawer();
          }

          return;
        }

        this.close();
      });

      this.btnClearText.addEventListener('click', e => {
        this.close();
        if (searchDrawerContent) searchDrawerContent.style.height = 'auto';
        toggleSearchSuggestionSections(true);
      });
    }

    async getSearchResults(searchTerm) {
      try {
        const encodedSearchTerm = encodeURIComponent(searchTerm);
        const predictiveSearchUrl = `${Shopify.routes.root}search/suggest?q=${encodedSearchTerm}&resources[type]=product,page,article,collection,query&resources[limit]=4&resources[limit_scope]=each&section_id=predictive-search`;
        const searchResultsUrl = `${Shopify.routes.root}search?q=${encodedSearchTerm}&type=product&options[prefix]=last`;

        const [predictiveResult, searchResultsResult] = await Promise.allSettled([
          fetch(predictiveSearchUrl),
          fetch(searchResultsUrl),
        ]);

        const predictiveResponse = predictiveResult.status === 'fulfilled'
          ? predictiveResult.value
          : null;
        const searchResultsResponse = searchResultsResult.status === 'fulfilled'
          ? searchResultsResult.value
          : null;

        if (!this.isOpen) {
          return;
        }

        if (!predictiveResponse?.ok) {
          const error = new Error(predictiveResponse?.status || 'Search request failed');

          throw error;
        }

        const predictiveText = await predictiveResponse.text();
        const predictiveDocument = new DOMParser().parseFromString(
          predictiveText,
          'text/html'
        );

        let searchResultCards = [];

        if (searchResultsResponse?.ok) {
          const searchResultsText = await searchResultsResponse.text();
          const searchResultsDocument = new DOMParser().parseFromString(
            searchResultsText,
            'text/html'
          );

          searchResultCards = Array.from(
            searchResultsDocument.querySelectorAll('#product-grid product-card')
          );
        }

        this.mergeSearchResultProducts(
          predictiveDocument,
          searchResultCards,
          4
        );

        const predictiveSection = predictiveDocument.querySelector(
          '#shopify-section-predictive-search'
        );
        const resultsMarkup = predictiveSection?.innerHTML || '';

        this.resultsContainer.innerHTML = resultsMarkup;
        this.open();

        // new added product cards - quick cart triggers - set event listeners
        document.querySelector('quick-cart-drawer')?.initTriggers();

      } catch (error) {
        this.errorMessageText.textContent = error.message;
        this.errorMessageContainer.classList.remove('hidden');
      }
    }

    getProductCardKey(card) {
      return card?.dataset?.trackKey || card?.dataset?.handle || '';
    }

    mergeSearchResultProducts(document, searchResultCards, minimumProducts) {
      let grid = document.querySelector('.predictive-search__grid');

      if (!grid && searchResultCards.length > 0) {
        grid = this.createProductGrid(document);
      }

      if (!grid) {
        return;
      }

      const predictiveCards = Array.from(grid.querySelectorAll('product-card'));
      const mergedCards = [];
      const seenKeys = new Set();

      [...predictiveCards, ...searchResultCards].forEach((card) => {
        if (mergedCards.length >= minimumProducts) {
          return;
        }

        const key = this.getProductCardKey(card);

        if (!key || seenKeys.has(key)) {
          return;
        }

        seenKeys.add(key);
        mergedCards.push(card.cloneNode(true));
      });

      grid.replaceChildren(...mergedCards);

      if (mergedCards.length > 0) {
        document.querySelector('.predictive-search__message')?.remove();
      }
    }

    createProductGrid(document) {
      const results = document.querySelector('#predictive-search-results');

      if (!results) {
        return null;
      }

      let productColumn = results.querySelector('.predictive-search__col-product');

      if (!productColumn) {
        productColumn = document.createElement('div');
        productColumn.className = 'predictive-search__col predictive-search__col-product';

        const viewResultsColumn = results
          .querySelector('.predictive-search__link')
          ?.closest('.predictive-search__col');

        if (viewResultsColumn) {
          results.insertBefore(productColumn, viewResultsColumn);
        } else {
          results.append(productColumn);
        }
      }

      productColumn.innerHTML = `
        <div class="predictive-search__row">
          <h6 class="search-drawer__title"></h6>
          <div class="predictive-search__grid"></div>
        </div>
      `;
      productColumn.querySelector('.search-drawer__title').textContent =
        this.dataset.productsLabel || '';

      return productColumn.querySelector('.predictive-search__grid');
    }

    open() {
      this.classList.add('is-open');
      this.resultsContainer.classList.remove('hidden');
      this.errorMessageContainer.classList.add('hidden');
      this.btnSubmit.setAttribute('tabindex', '-1');
      this.btnClearText.removeAttribute('disabled');
    }

    close() {
      this.isOpen = false;
      this.classList.remove('is-open');
      this.resultsContainer.classList.add('hidden');
      this.errorMessageContainer.classList.add('hidden');
      this.closest('.drawer__content--search')
        ?.querySelectorAll('.search-drawer__suggestions, .search-most-loved-styles')
        .forEach(section => {
          section.style.display = '';
        });
      this.input.value = '';
      this.input.focus();
      this.btnSubmit.removeAttribute('tabindex');
      this.btnClearText.setAttribute('disabled', true);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
